import {OrgFilterProvider} from '@github-ui/filter-core/providers'
import {useQuery, useQueryClient} from '@github-ui/react-query'
import {getDefaultReposProviders} from '@github-ui/repos-filter/providers'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'

import {adjustProvidersToScope} from '../helper/adjust-providers'
import {reposPickerDefinitionsPath} from '../paths'
import type {PickerScope, PropertyDefinition} from '../types'

const DEFINITIONS_STALE_TIME_IN_MS = 1000 * 60 * 5 // 5 minutes

const DEFINITIONS_QUERY_KEY = ['repos-picker', 'definitions'] as const

export function useDefaultReposProviders(scope: PickerScope) {
  const {data: {definitions = []} = {}} = useQueryDefinitions({scope, extensions: ['external']})
  const providers = getDefaultReposProviders(definitions, undefined, {
    withOwnerTerms: scope.type === 'unbounded',
    withFinderTerms: true,
    withRelevanceSort: true,
  })

  if (scope.type === 'enterprise' || scope.type === 'unbounded') {
    const businessSlug = scope.type === 'enterprise' ? scope.slug : undefined
    providers.push(
      new OrgFilterProvider({
        businessSlug,
        filterTypes: {valueless: false, multiKey: true, multiValue: true},
      }),
    )
  }

  return adjustProvidersToScope(providers, scope)
}

type ReposPickerDefinitionsResponse = {
  definitions?: PropertyDefinition[]
}

export function useQueryDefinitions({
  scope,
  extensions,
  enabled = true,
}: {
  scope: PickerScope
  extensions?: string[]
  enabled?: boolean
}) {
  const isBizOrOrg = scope.type === 'enterprise' || scope.type === 'organization'

  return useQuery<ReposPickerDefinitionsResponse>({
    queryKey: [...DEFINITIONS_QUERY_KEY, scope, extensions],
    enabled: isBizOrOrg && enabled,
    queryFn: async () => {
      const fetchDefinitionsUrl = reposPickerDefinitionsPath(scope, extensions)

      const response = await verifiedFetchJSON(fetchDefinitionsUrl)
      if (!response.ok) return {}

      const responseData = await response.json()
      return responseData as ReposPickerDefinitionsResponse
    },
    staleTime: DEFINITIONS_STALE_TIME_IN_MS,
  })
}

/**
 * Returns a callback that invalidates the react-query cache for the repos-picker
 * definitions list.
 */
export function useInvalidateDefinitionsQuery() {
  const queryClient = useQueryClient()
  return useCallback(() => queryClient.invalidateQueries({queryKey: DEFINITIONS_QUERY_KEY}), [queryClient])
}
